import { DialogProps, Dialog as MuiDialog, styled } from '@mui/material';
import React from 'react';
import { Task, TaskGroup } from '../../../apis/tasksApi';
import CrossSvgIcon from '../../../components/SvgIcon/CrossSvgIcon';
import TaskStatusIcon from '../TaskStatusIcon';
import {
  DEFAULT_DASLOOP_PROTOCOL_TASK_TIMEOUT,
  getCreateDate,
  getFinishDate,
  getTaskGroupType,
  getTextStatus,
} from '../../../utils/constant';
import { TaskStatus } from '../../../types';
import { format } from 'date-fns';
import { DasEndpoint } from '../../../types/Device';

interface ContainerProps {
  status: TaskStatus;
}

const Span = styled('span')<ContainerProps>`
  color: ${({ status, theme }) => {
    switch (status) {
      case 'inProgress':
        return theme.color.primary.$100;
      case 'completed':
        return theme.color.green.$100;
      case 'failed':
        return theme.color.highlight;
      case 'canceled':
      default:
        return 'inherit';
    }
  }};
  font-family: 'Noto Sans';
  font-size: 18px;
  font-style: 'bold';
  font-weight: 500;
`;
const Dialog = styled(MuiDialog)(({ theme }) => {
  return {
    '& .MuiPaper-root': {
      minWidth: '600px',
      '& > .header': {
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        height: '64px',
        padding: '16px',
        backgroundColor: theme.color.box_bbg,
        '& > .title': {
          ...theme.typography.body1,
          fontStyle: 'bold',
          fontWeight: '500',
          textAlign: 'center',
        },
        '& > .title-end': {
          ...theme.typography.body1,
          fontStyle: 'bold',
          fontWeight: '500',
          textAlign: 'center',
          marginLeft: 'auto',
        },
        '& > .icon': {
          ...theme.typography.body1,
          fontWeight: '500',
          textAlign: 'center',
        },
        '& > .close-icon': {
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',

          '&:before': {
            display: 'block',
            content: '""',
            width: '2px',
            height: '30px',
            backgroundColor: theme.color.secondary.$40,
            marginRight: '20px',
          },
        },
      },
      '& > .banner': {
        color: '#fff',
        fontSize: '1rem',
        padding: '16px',
        '&[data-status="error"]': {
          background: theme.color.highlight,
        },
        '&[data-status="success"]': {
          background: theme.color.green.$100,
        },
      },
      '& > .content': {
        maxHeight: '640px',
        padding: '24px',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '15px',
        '& > .row': {
          '& > .container': {
            padding: '0px',
            marginTop: '10px',
            '& > .title': {
              fontFamily: 'Noto Sans',
              fontSize: '14px',
              color: theme.color.secondary.$60,
              padding: '0px',
              margin: '0px',
            },
            '& > .description': {
              fontFamily: 'Noto Sans',
              fontSize: '16px',
              padding: '0px',
              margin: '0px',
              color: theme.color.secondary.$100,
            },
          },
        },
      },

      '& > .badge': {
        ...theme.typography.body1,
        padding: '10px 24px',
        margin: '10px 24px',
        width: 'calc(100% - 48px)',
        display: 'flex',
        height: '44px',
        backgroundColor: 'rgb(230,166,0, 20%)',
        borderRadius: '4px',
        border: '1px solid #E6A600',
      },

      '& > .action': {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '24px',
      },
    },
  };
});

interface PanelInformationDetailProps extends DialogProps {
  task: Task | undefined;
  taskGroup: TaskGroup | undefined;
  deviceMap: {
    [index: string]: DasEndpoint;
  };
}

export const PanelInformationDetail: React.FC<PanelInformationDetailProps> = ({
  open,
  onClose,
  task,
  taskGroup,
  deviceMap,
  ...props
}) => {
  const handleClose = (e) => {
    onClose?.(e, 'backdropClick');
  };
  const createDate = taskGroup
    ? format(getCreateDate(taskGroup), 'yyyy-MM-dd hh:mm:ss')
    : 'N/A';
  const finishDate = taskGroup
    ? format(getFinishDate(taskGroup), 'yyyy-MM-dd hh:mm:ss')
    : 'N/A';

  const device = task?.endpointId ? deviceMap?.[task?.endpointId] : undefined;

  const timeoutTime = DEFAULT_DASLOOP_PROTOCOL_TASK_TIMEOUT * 2;

  const created_at = task?.createdAt ? new Date(task.createdAt) : new Date();
  const expiryDate = new Date(created_at.getTime() + timeoutTime);

  return (
    <Dialog open={open} onClose={handleClose} {...props}>
      <div className="header">
        {task && (
          <>
            <TaskStatusIcon status={task?.status} />
            <Span status={task.status} className="icon">
              {getTextStatus(task.status)}
            </Span>
          </>
        )}
        <span className="title">{taskGroup?.name}</span>
        <span className="title-end">
          {getTaskGroupType(taskGroup?.type ?? '')}
        </span>
        <div className="close-icon" onClick={handleClose}>
          <CrossSvgIcon sx={{ width: '40px', height: '40px' }} />
        </div>
      </div>
      <div className="content">
        <div className="row">
          <div className="container">
            <p className="title">Create Date</p>
            <p className="description">{createDate}</p>
          </div>
          <div className="container">
            <p className="title">DasID</p>
            <p className="description">{device?.dasId}</p>
          </div>
          <div className="container">
            <p className="title">Type</p>
            <p className="description">
              {getTaskGroupType(taskGroup?.type ?? '')}
            </p>
          </div>
          <div className="container">
            <p className="title">Content</p>
            <p className="description">{taskGroup?.content}</p>
          </div>
          <div className="container">
            <p className="title">Repeats</p>
            <p className="description">{task?.payload?.repeats}</p>
          </div>
          <div className="container">
            <p className="title">Delay(s)</p>
            <p className="description">{task?.payload?.delay}</p>
          </div>
          <div className="container">
            <p className="title">Expiry Date</p>
            <p className="description">
              {format(expiryDate, 'yyyy-MM-dd hh:mm:ss')}
            </p>
          </div>
        </div>
        <div className="row">
          <div className="container">
            <p className="title">Finish Date</p>
            <p className="description">{finishDate}</p>
          </div>
        </div>
      </div>
      <div className="badge">Any Channel (NBIoT, WiFi, DasMain, etc)</div>
      <div className="container">
        <pre
          style={{
            maxHeight: '300px',
            overflowY: 'auto',
            padding: '1rem',
            backgroundColor: '#FAFAFA',
            fontSize: '14px',
          }}
        >
          Result:
          <hr />
          {JSON.stringify(task?.result, null, 2)}
        </pre>
      </div>
    </Dialog>
  );
};
